import {
    Cell,
    CellEditorHandler,
    CellHighlight,
    CellMarker,
    CellOverlay,
    CellRenderer,
    CellState,
    Client,
    Clipboard,
    ConnectionConstraint,
    ConnectionHandler,
    ConstraintHandler,
    DragSource,
    EdgeHandlerConfig,
    EdgeHandler,
    ElbowEdgeHandler,
    EllipseShape,
    EventObject,
    EventSource,
    Geometry,
    Graph as MaxGraph,
    GraphDataModel,
    GraphView,
    Guide,
    HandleConfig,
    HierarchicalLayout,
    ImageBox,
    ImageShape,
    InternalEvent,
    InternalMouseEvent,
    KeyHandler,
    LayoutManager,
    MaxPopupMenu,
    Outline,
    PanningHandler,
    Point,
    PolylineShape,
    PopupMenuHandler,
    Rectangle,
    RectangleShape,
    RubberBandHandler,
    SelectionHandler,
    Shape,
    ShapeRegistry,
    StackLayout,
    StencilShapeRegistry,
    StyleDefaultsConfig,
    Stylesheet,
    SvgCanvas2D,
    Translations,
    UndoManager,
    VertexHandlerConfig,
    VertexHandler,
    cellArrayUtils,
    cloneUtils,
    domUtils,
    eventUtils,
    mathUtils,
    styleUtils,
    constants,
} from '@maxgraph/core';
import {defaultBundle} from './defaultBundle';

const parseLegacyStyle = style => {
    if (style == null || typeof style !== 'string') {
        return style || {};
    }

    return style.split(';').filter(Boolean).reduce((result, token) => {
        const separatorIndex = token.indexOf('=');

        if (separatorIndex < 0) {
            result.baseStyleNames = result.baseStyleNames || [];
            result.baseStyleNames.push(token);
        } else {
            result[token.substring(0, separatorIndex)] = token.substring(separatorIndex + 1);
        }

        return result;
    }, {});
};

const numericStyleKeys = new Set([
    'arcSize',
    'fontSize',
    'fontStyle',
    'imageHeight',
    'imageWidth',
    'labelPadding',
    'labelWidth',
    'opacity',
    'perimeterSpacing',
    'rotation',
    'sourcePerimeterSpacing',
    'spacing',
    'spacingBottom',
    'spacingLeft',
    'spacingRight',
    'spacingTop',
    'startSize',
    'strokeWidth',
    'targetPerimeterSpacing',
]);

const booleanStyleKeys = new Set([
    'absoluteArcSize',
    'bendable',
    'cloneable',
    'connectable',
    'dashed',
    'deletable',
    'editable',
    'foldable',
    'html',
    'movable',
    'resizable',
    'rotatable',
    'rounded',
    'selectable',
    'shadow',
]);

const normalizeLegacyStyleValue = (key, value) => {
    if (typeof value !== 'string') {
        return value;
    }

    const trimmedValue = value.trim();
    if (numericStyleKeys.has(key) && trimmedValue !== '') {
        const numericValue = Number(trimmedValue);
        return Number.isNaN(numericValue) ? value : numericValue;
    }

    if (booleanStyleKeys.has(key)) {
        if (trimmedValue === '1' || trimmedValue === 'true') {
            return true;
        }
        if (trimmedValue === '0' || trimmedValue === 'false') {
            return false;
        }
    }

    return value;
};

const normalizeLegacyStyle = style => Object.keys(style || {}).reduce((result, key) => {
    result[key] = normalizeLegacyStyleValue(key, style[key]);
    return result;
}, {});

const serializeLegacyStyle = style => {
    if (style == null || typeof style === 'string') {
        return style || '';
    }

    const baseStyleNames = Array.isArray(style.baseStyleNames) ? style.baseStyleNames : [];
    const entries = Object.keys(style)
        .filter(key => key !== 'baseStyleNames' && key !== 'ignoreDefaultStyle' && style[key] != null)
        .map(key => `${key}=${style[key]}`);

    return baseStyleNames.concat(entries).join(';');
};

const setStyle = (style, key, value) => {
    const parsedStyle = typeof style === 'string' || style == null ?
        parseLegacyStyle(style) :
        {
            ...style,
            baseStyleNames: Array.isArray(style.baseStyleNames) ? [...style.baseStyleNames] : style.baseStyleNames
        };

    if (value == null || value === constants.NONE) {
        delete parsedStyle[key];
    } else {
        parsedStyle[key] = value;
    }

    return typeof style === 'string' ? serializeLegacyStyle(parsedStyle) : parsedStyle;
};

const originalGetCellStyle = Stylesheet.prototype.getCellStyle;

HandleConfig.fillColor = '#99ccff';
HandleConfig.strokeColor = '#0088cf';
HandleConfig.labelFillColor = '#cee7ff';
EdgeHandlerConfig.connectFillColor = '#cee7ff';
EdgeHandlerConfig.handleShape = 'circle';
EdgeHandlerConfig.selectionColor = '#00a8ff';
EdgeHandlerConfig.virtualBendsEnabled = true;
StyleDefaultsConfig.shadowColor = '#000000';
StyleDefaultsConfig.shadowOpacity = 0.25;
VertexHandlerConfig.selectionColor = '#00a8ff';

Stylesheet.prototype.getCellStyle = function (cellStyle, defaultStyle) {
    return normalizeLegacyStyle(originalGetCellStyle.call(
        this,
        parseLegacyStyle(cellStyle),
        defaultStyle
    ));
};

GraphDataModel.prototype.getStyle = GraphDataModel.prototype.getStyle || function (cell) {
    return cell?.getStyle();
};

GraphDataModel.prototype.getChildAt = GraphDataModel.prototype.getChildAt || function (cell, index) {
    return cell?.getChildAt(index);
};

GraphDataModel.prototype.getChildCount = GraphDataModel.prototype.getChildCount || function (cell) {
    return cell?.getChildCount?.() || 0;
};

GraphDataModel.prototype.getChildren = GraphDataModel.prototype.getChildren || function (cell) {
    return cell?.getChildren?.() || [];
};

GraphDataModel.prototype.getEdgeCount = GraphDataModel.prototype.getEdgeCount || function (cell) {
    return cell?.getEdgeCount?.() || 0;
};

GraphDataModel.prototype.getEdges = GraphDataModel.prototype.getEdges || function (cell, incoming = true, outgoing = true, includeLoops = true) {
    return (cell?.getEdges?.() || []).filter(edge => {
        const source = edge.getTerminal(true);
        const target = edge.getTerminal(false);

        if (source === target) {
            return includeLoops;
        }

        return (incoming && target === cell) || (outgoing && source === cell);
    });
};

GraphDataModel.prototype.getEdgesBetween = GraphDataModel.prototype.getEdgesBetween || function (source, target, directed = false) {
    return this.getEdges(source, true, true, false).filter(edge => {
        const edgeSource = edge.getTerminal(true);
        const edgeTarget = edge.getTerminal(false);

        return (edgeSource === source && edgeTarget === target) ||
            (!directed && edgeSource === target && edgeTarget === source);
    });
};

GraphDataModel.prototype.getGeometry = GraphDataModel.prototype.getGeometry || function (cell) {
    return cell?.getGeometry();
};

GraphDataModel.prototype.getParent = GraphDataModel.prototype.getParent || function (cell) {
    return cell?.getParent();
};

GraphDataModel.prototype.getParents = GraphDataModel.prototype.getParents || function (cells) {
    const parents = [];

    (cells || []).forEach(cell => {
        const parent = this.getParent(cell);
        if (parent != null && !parents.includes(parent)) {
            parents.push(parent);
        }
    });

    return parents;
};

GraphDataModel.prototype.isAncestor = GraphDataModel.prototype.isAncestor || function (parent, child) {
    return parent?.isAncestor?.(child) === true;
};

GraphDataModel.prototype.getTopmostCells = GraphDataModel.prototype.getTopmostCells || function (cells) {
    return cellArrayUtils.getTopmostCells(cells || []);
};

GraphDataModel.prototype.getTerminal = GraphDataModel.prototype.getTerminal || function (cell, isSource) {
    return cell?.getTerminal(isSource);
};

GraphDataModel.prototype.getValue = GraphDataModel.prototype.getValue || function (cell) {
    return cell?.getValue();
};

GraphDataModel.prototype.isEdge = GraphDataModel.prototype.isEdge || function (cell) {
    return cell?.isEdge?.() === true;
};

GraphDataModel.prototype.isVertex = GraphDataModel.prototype.isVertex || function (cell) {
    return cell?.isVertex?.() === true;
};

GraphDataModel.prototype.isVisible = GraphDataModel.prototype.isVisible || function (cell) {
    return cell?.isVisible?.() === true;
};

GraphDataModel.prototype.isCollapsed = GraphDataModel.prototype.isCollapsed || function (cell) {
    return cell?.isCollapsed?.() === true;
};

MaxGraph.prototype.getModel = MaxGraph.prototype.getModel || function () {
    return this.getDataModel();
};

MaxGraph.prototype.getCellGeometry = MaxGraph.prototype.getCellGeometry || function (cell) {
    return this.getDataModel().getGeometry(cell);
};

MaxGraph.prototype.getCellStyle = MaxGraph.prototype.getCellStyle || function (cell) {
    return this.getDataModel().getStyle(cell);
};

MaxGraph.prototype.isCellVisible = MaxGraph.prototype.isCellVisible || function (cell) {
    return this.getDataModel().isVisible(cell);
};

MaxGraph.prototype.isCellCollapsed = MaxGraph.prototype.isCellCollapsed || function (cell) {
    return this.getDataModel().isCollapsed(cell);
};

MaxGraph.prototype.isCellConnectable = MaxGraph.prototype.isCellConnectable || function (cell) {
    return cell?.isConnectable?.() === true;
};

MaxGraph.prototype.getTolerance = MaxGraph.prototype.getTolerance || function () {
    return this.tolerance;
};

const connectionConstraintStyleKeys = source => source ?
    ['exitX', 'exitY', 'exitDx', 'exitDy', 'exitPerimeter'] :
    ['entryX', 'entryY', 'entryDx', 'entryDy', 'entryPerimeter'];

const clearConnectionConstraintStyle = (graph, edge, source) => {
    if (!edge) {
        return;
    }

    const model = graph.getDataModel();
    const currentStyle = edge.getStyle();
    const style = typeof currentStyle === 'string' ?
        parseLegacyStyle(currentStyle) :
        edge.getClonedStyle();

    connectionConstraintStyleKeys(source).forEach(key => delete style[key]);
    model.setStyle(edge, typeof currentStyle === 'string' ? serializeLegacyStyle(style) : style);
};

const maxGraphSetConnectionConstraint = MaxGraph.prototype.setConnectionConstraint;
MaxGraph.prototype.setConnectionConstraint = function (edge, terminal, source = false, constraint = null) {
    if (!constraint || !constraint.point) {
        this.getDataModel().batchUpdate(() => clearConnectionConstraintStyle(this, edge, source));
        return;
    }

    maxGraphSetConnectionConstraint.call(this, edge, terminal, source, constraint);
};

ConnectionHandler.prototype.init = ConnectionHandler.prototype.init || function () {};

EdgeHandler.prototype.init = EdgeHandler.prototype.init || function () {};
EdgeHandler.prototype.destroy = EdgeHandler.prototype.destroy || function () {
    this.onDestroy?.();
};

VertexHandler.prototype.destroy = VertexHandler.prototype.destroy || function () {
    this.onDestroy?.();
};

const definePluginAccessor = (property, pluginId) => {
    if (Object.prototype.hasOwnProperty.call(MaxGraph.prototype, property)) {
        return;
    }

    Object.defineProperty(MaxGraph.prototype, property, {
        configurable: true,
        get() {
            return this.getPlugin(pluginId);
        }
    });
};

definePluginAccessor('cellEditor', 'CellEditorHandler');
definePluginAccessor('connectionHandler', 'ConnectionHandler');
definePluginAccessor('graphHandler', 'SelectionHandler');
definePluginAccessor('panningHandler', 'PanningHandler');
definePluginAccessor('popupMenuHandler', 'PopupMenuHandler');
definePluginAccessor('selectionCellsHandler', 'SelectionCellsHandler');
definePluginAccessor('tooltipHandler', 'TooltipHandler');

PanningHandler.prototype.isActive = function () {
    return this.active || this.initialScale !== 0;
};

Translations.loadDefaultBundle = false;

const translationsGet = Translations.get;
Translations.get = (key = null, params = null, defaultValue = null) =>
    translationsGet(key, params, defaultValue ?? key);

export const mxResources = Translations;

mxResources.parse(Object.keys(defaultBundle).map(key => `${key}=${defaultBundle[key]}`).join('\n'));

export function mxEventSource(eventSource = null) {
    this.eventListeners = [];
    this.eventsEnabled = true;
    this.eventSource = eventSource;
}

mxEventSource.prototype = EventSource.prototype;
mxEventSource.prototype.constructor = mxEventSource;

export class mxDictionary {
    constructor() {
        this.map = new Map();
    }

    clear() {
        this.map.clear();
    }

    get(key) {
        return this.map.get(key);
    }

    getKeys() {
        return Array.from(this.map.keys());
    }

    put(key, value) {
        this.map.set(key, value);
        return value;
    }

    remove(key) {
        const value = this.map.get(key);
        this.map.delete(key);
        return value;
    }

    visit(visitor) {
        this.map.forEach((value, key) => visitor(key, value));
    }
}

export const mxConstants = {
    ...constants,
    ALIGN_CENTER: 'center',
    ALIGN_BOTTOM: 'bottom',
    ALIGN_LEFT: 'left',
    ALIGN_MIDDLE: 'middle',
    ALIGN_RIGHT: 'right',
    ALIGN_TOP: 'top',
    CURSOR_LABEL_HANDLE: 'move',
    DIALECT_SVG: 'svg',
    DIRECTION_EAST: 'east',
    DIRECTION_NORTH: 'north',
    DIRECTION_SOUTH: 'south',
    DIRECTION_WEST: 'west',
    ELBOW_HORIZONTAL: 'horizontal',
    ELBOW_VERTICAL: 'vertical',
    NONE: 'none',
    SHAPE_RECTANGLE: 'rectangle',
    STYLE_CURVED: 'curved',
    STYLE_DASHED: 'dashed',
    STYLE_EDGE: 'edgeStyle',
    STYLE_ELBOW: 'elbow',
    STYLE_ENDARROW: 'endArrow',
    STYLE_ENDFILL: 'endFill',
    STYLE_ENDSIZE: 'endSize',
    STYLE_FILLCOLOR: 'fillColor',
    STYLE_GRADIENTCOLOR: 'gradientColor',
    STYLE_LABEL_BACKGROUNDCOLOR: 'labelBackgroundColor',
    STYLE_PERIMETER_SPACING: 'perimeterSpacing',
    STYLE_RESIZABLE: 'resizable',
    STYLE_ROTATION: 'rotation',
    STYLE_SHAPE: 'shape',
    STYLE_STARTARROW: 'startArrow',
    STYLE_STARTFILL: 'startFill',
    STYLE_STARTSIZE: 'startSize',
    STYLE_SOURCE_PERIMETER_SPACING: 'sourcePerimeterSpacing',
    STYLE_STROKECOLOR: 'strokeColor',
    STYLE_TARGET_PERIMETER_SPACING: 'targetPerimeterSpacing',
    STYLE_WHITE_SPACE: 'whiteSpace',
};

export const mxEvent = {
    ...InternalEvent,
    ...eventUtils,
    addGestureListeners: InternalEvent.addGestureListeners,
    addListener: InternalEvent.addListener,
    addMouseWheelListener: InternalEvent.addMouseWheelListener,
    consume: InternalEvent.consume,
    isConsumed: eventUtils.isConsumed,
    release: InternalEvent.release,
    redirectMouseEvents: InternalEvent.redirectMouseEvents,
    removeGestureListeners: InternalEvent.removeGestureListeners,
    removeListener: InternalEvent.removeListener,
};

export const mxUtils = {
    ...cellArrayUtils,
    ...cloneUtils,
    ...domUtils,
    ...mathUtils,
    ...styleUtils,

    bind: (scope, fn) => fn.bind(scope),
    clone: cloneUtils.clone,
    contains: mathUtils.contains,
    convertPoint: styleUtils.convertPoint,
    createImage: domUtils.createImage,
    equalPoints: (a, b) => {
        if (a == null || b == null || a.length !== b.length) {
            return false;
        }

        return a.every((point, index) => point.equals ? point.equals(b[index]) :
            point?.x === b[index]?.x && point?.y === b[index]?.y);
    },
    extend: (child, parent) => {
        child.prototype = Object.create(parent.prototype);
        child.prototype.constructor = child;
    },
    getTextContent: domUtils.getTextContent,
    getValue: (object, key, defaultValue = null) => object?.[key] ?? defaultValue,
    hasScrollbars: styleUtils.hasScrollbars,
    indexOf: (array, value) => array.indexOf(value),
    makeDraggable: (element, graph, function_, dragElement, dx, dy, autoscroll, scalePreview, highlightDropTargets, getDropTarget) => {
        const dragSource = new DragSource(element, function_);

        dragSource.dragElement = dragElement;
        dragSource.dx = dx;
        dragSource.dy = dy;
        dragSource.autoscroll = autoscroll;
        dragSource.scalePreview = scalePreview;
        dragSource.highlightDropTargets = highlightDropTargets;

        if (getDropTarget) {
            dragSource.getDropTarget = getDropTarget;
        }

        dragSource.getGraphForEvent = function () {
            return graph;
        };

        dragSource.drop = function (currentGraph, evt, dropTarget = null, x, y, force) {
            this.dropHandler(currentGraph, evt, dropTarget, x, y, force);

            if (currentGraph.container.style.visibility !== 'hidden') {
                currentGraph.container.focus();
            }
        };

        return dragSource;
    },
    setStyle,
    trim: value => value?.trim(),
    write: domUtils.write,
};

export const mxCell = Cell;
export const mxCellEditor = CellEditorHandler;
export const mxCellHighlight = CellHighlight;
export const mxCellMarker = CellMarker;
export const mxCellOverlay = CellOverlay;
export const mxCellRenderer = CellRenderer;
export const mxCellState = CellState;
export const mxClient = Client;
export const mxClipboard = Clipboard;
export const mxConnectionConstraint = ConnectionConstraint;
export const mxConnectionHandler = ConnectionHandler;
export const mxConstraintHandler = ConstraintHandler;
export const mxDragSource = DragSource;
export const mxEdgeHandler = EdgeHandler;
export const mxElbowEdgeHandler = ElbowEdgeHandler;
export const mxEllipse = EllipseShape;
export const mxEventObject = EventObject;
export const mxGeometry = Geometry;
export const mxGraph = MaxGraph;
export const mxGraphHandler = SelectionHandler;
export const mxGraphModel = GraphDataModel;
export const mxGraphView = GraphView;
export const mxGuide = Guide;
export const mxHierarchicalLayout = HierarchicalLayout;
export const mxImage = ImageBox;
export const mxImageShape = ImageShape;
export const mxKeyHandler = KeyHandler;
export const mxLayoutManager = LayoutManager;
export const mxMouseEvent = InternalMouseEvent;
export const mxOutline = Outline;
export const mxPanningHandler = PanningHandler;
export const mxPoint = Point;
export const mxPolyline = PolylineShape;
export const mxPopupMenu = MaxPopupMenu;
export const mxPopupMenuHandler = PopupMenuHandler;
export const mxRectangle = Rectangle;
export const mxRectangleShape = RectangleShape;
export const mxRubberband = RubberBandHandler;
export const mxShape = Shape;
export const mxShapeRegistry = ShapeRegistry;
export const mxStackLayout = StackLayout;
export const mxStencilRegistry = StencilShapeRegistry;
export const mxStylesheet = Stylesheet;
export const mxSvgCanvas2D = SvgCanvas2D;
export const mxUndoManager = UndoManager;
export const mxVertexHandler = VertexHandler;
